import React from 'react';
import {
  BadgeLabelList,
  MAX_VISIBLE_BADGES_DESKTOP,
  MAX_VISIBLE_BADGES_MOBILE,
} from '@/components/orders/badge-label-list';
import { BORDER, TYPOGRAPHY } from '@/styles';
import {
  DISPLAY_SIZE_TO_IMAGE_SIZE,
  OrderableItemListItemImage,
} from '@/components/orders/orderable-item-list-item-image';
import { ItemListOption } from '@/contexts/item-context-types';
import { ListDisplaySize } from '@/contexts/order-guide-settings-context';
import { OrderableItemListItemHeading } from '@/components/orders/orderable-item-list-item-heading';
import { StyleProp, StyleSheet, Text, View, ViewStyle } from 'react-native';
import {
  useIsPhotoHidden,
  useItemDisplayOptions,
} from '@/contexts/new-order-context';
import { useLayoutContext } from '@/contexts/layout-context';

interface Props {
  containerStyle?: StyleProp<ViewStyle>;
  isCompactView?: boolean;
  listDisplaySize?: ListDisplaySize;
  option: ItemListOption;
  rightComponent?: React.ReactNode;
  style?: StyleProp<ViewStyle>;
  titleNumberOfLines?: number;
}

export const OrderableItemListItemContent: React.FC<Props> = ({
  containerStyle,
  isCompactView = false,
  listDisplaySize = ListDisplaySize.Standard,
  option,
  rightComponent,
  style,
  titleNumberOfLines = 2,
}) => {
  const imageSize = DISPLAY_SIZE_TO_IMAGE_SIZE[listDisplaySize];
  const isPhotoHidden = useIsPhotoHidden(option.metadata!, listDisplaySize);
  const photoSizeStyle = {
    height: imageSize,
    width: imageSize,
  };
  const altText = [option.metadata?.displayName, option.metadata?.externalID]
    .filter(Boolean)
    .join('-');
  const { hidePlaceholderImages } = useItemDisplayOptions();
  const { isSmallLayout, isSmallTabletLayout } = useLayoutContext();
  const badgeMaxVisible =
    isSmallLayout || isSmallTabletLayout
      ? MAX_VISIBLE_BADGES_MOBILE
      : MAX_VISIBLE_BADGES_DESKTOP;

  const image = (
    <View
      style={[
        styles.imageContainer,
        photoSizeStyle,
        isCompactView ? styles.compactViewImageContainer : null,
      ]}
    >
      <OrderableItemListItemImage
        alt={altText}
        hidePlaceholderImages={hidePlaceholderImages}
        imageURLs={option.metadata?.photoURLs || []}
        listDisplaySize={listDisplaySize}
      />
    </View>
  );

  if (isCompactView) {
    return !isPhotoHidden ? image : null;
  }

  return (
    <View style={[styles.base, containerStyle]}>
      {!isPhotoHidden ? image : null}
      <View style={[styles.textContainer, style]}>
        <OrderableItemListItemHeading
          heading={option.title}
          numberOfLines={
            option.metadata?.badges?.length ? 1 : titleNumberOfLines
          }
        />
        {option.metadata?.description ? (
          <Text
            numberOfLines={option.metadata?.badges?.length ? 1 : 2}
            style={TYPOGRAPHY.description}
          >
            {option.metadata.description}
          </Text>
        ) : null}
        <BadgeLabelList
          badges={option.metadata?.badges}
          iconOnly
          maxVisible={badgeMaxVisible}
        />
      </View>
      {rightComponent}
    </View>
  );
};

const styles = StyleSheet.create({
  base: {
    flexDirection: 'row',
  },
  compactViewImageContainer: {
    marginRight: 0,
  },
  imageContainer: {
    ...BORDER.primary,
    alignItems: 'flex-end',
    marginRight: 16,
    overflow: 'hidden',
  },
  textContainer: {
    flex: 1,
  },
});
